import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, closeDialog, DataStatus, FlagProps, openDialog} from 'platform/components';

import {useEffect, useRef, useState} from 'react';

import {find, isNil, not} from 'ramda';
import {isNotNil, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetCommentsQuery} from '@dms/api/core';
import {
  useGetCustomerV2Query,
  useLazyGetVehicleCustomerQuery,
  usePostVehicleCustomerMutation,
} from '@dms/api/customer';
import {useGetAdditionalNoteQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleLocalActionNoteQuery} from '@dms/api/vehicle';
import {
  useDeleteServiceCaseMutation,
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseJobsQuery,
  usePatchServiceCaseMutation,
  usePostAvailableXentryOrdersMutation,
  usePostIntegrationsForXentryLinkMutation,
  usePostServiceCaseCreateOrdersMutation,
  usePostServiceCaseIntegrationsXentryOrderManualCreationMutation,
  usePostServiceCaseMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  CustomerVehicleSelection,
  EntityHeader,
  EntityHeaderControl,
  getCustomerName,
  handleApiError,
  LinkXentry,
  Page,
  queryParams,
  Section,
  useStateFlag,
  workshopSearchParams,
} from '@dms/shared';
import {JobFormRef, ServiceCaseDetailActionsProvider} from '@dms/shared-aftersales';

import {
  buildArray,
  composePath,
  getApiDateString,
  Nullish,
  useQueryState,
  useNavigate,
} from 'shared/web';

import {SOURCE_DATA_GRID} from 'features/datagrid';

import {IntegrationsDropdown} from '../../components/IntegrationsDropdown/IntegrationsDropdown';
import {useWorkshopUrl} from '../../hooks/useWorkshopUrl';
import {ServiceJobsSelection} from './(sections)/ServiceRequest/ServiceJobsSelection';
import {useServiceCaseVehicleDraftParameters} from './hooks/useServiceCaseVehicleDraftParameters';

const OPERATOR_TYPE = 'CORVEHICLECUSTOMERTYPE_OPERATOR';

export function CreateServiceCase() {
  return (
    <ServiceCaseDetailActionsProvider>
      <CreateServiceCaseContent />
    </ServiceCaseDetailActionsProvider>
  );
}

function CreateServiceCaseContent() {
  const navigate = useNavigate();
  const {serviceCaseId} = useWorkshopUrl();
  const jobFormRef = useRef<JobFormRef | null>(null);
  const [sectionTab, setSectionTab] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const [source] = useQueryState(queryParams.REDIRECT_SOURCE);

  const [defaultCustomerId] = useQueryState('customerId');
  const [defaultVehicleId] = useQueryState('vehicleId');

  const [customerId, setCustomerId] = useState<string | Nullish>(defaultCustomerId);
  const [vehicleId, setVehicleId] = useState<string | Nullish>(defaultVehicleId);
  const [isCustomerVehicleChanged, setCustomerVehicleChanged] = useState<boolean>(false);

  const [postServiceCaseIntegrationsXentryOrderManualCreation] =
    usePostServiceCaseIntegrationsXentryOrderManualCreationMutation();
  const [postIntegrationsForXentryLink] = usePostIntegrationsForXentryLinkMutation();
  const [postAvailableXentryOrders] = usePostAvailableXentryOrdersMutation({
    fixedCacheKey: 'open-link-xentry',
  });
  const [postServiceCase, {isLoading: isPostServiceCaseLoading}] = usePostServiceCaseMutation();
  const [patchServiceCase, {isLoading: isPatchServiceCaseLoading}] = usePatchServiceCaseMutation();
  const [postVehicleCustomer, {isLoading: isPostVehicleCustomerLoading}] =
    usePostVehicleCustomerMutation();
  const [getVehicleCustomer] = useLazyGetVehicleCustomerQuery();
  const [deleteServiceCase, deleteServiceCaseQuery] = useDeleteServiceCaseMutation();
  const [postServiceCaseCreateOrders, {isLoading: isPostServiceCaseCreate}] =
    usePostServiceCaseCreateOrdersMutation();
  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId}, {skip: not(serviceCaseId)});
  const {
    data: jobs,
    isLoading: isServiceCaseJobsLoading,
    isError: isServiceCaseJobsError,
  } = useGetServiceCaseJobsQuery({serviceCaseId}, {skip: not(serviceCaseId)});
  const {data: vehicle, isError: isVehicleError} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: not(vehicleId)}
  );

  const {data: comments = []} = useGetCommentsQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: vehicleId ?? '',
    },
    {skip: not(vehicleId)}
  );

  const {data: additionalNote} = useGetAdditionalNoteQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: not(vehicleId)}
  );

  const hasComments = comments.length > 0;
  const hasBookingUrl = isNotNilOrEmpty(serviceCase?.bookingUrl);

  useEffect(() => {
    if (serviceCase && serviceCase?.state !== 'DRAFT') {
      const searchParams = new URLSearchParams(window.location.search);
      navigate(
        composePath(workshopRoutes.serviceCaseDetail, {
          params: {id: serviceCaseId},
          queryParams: {
            [queryParams.REDIRECT_SOURCE]: 'service-cases',
            [SOURCE_DATA_GRID]: searchParams.get(SOURCE_DATA_GRID),
          },
        })
      );
    }
  }, [serviceCase?.state]);

  useEffect(() => {
    if (isNotNil(serviceCase)) {
      setCustomerId(serviceCase?.customerId);
      setVehicleId(serviceCase?.vehicleId);
    }
  }, [serviceCase]);

  const {data: serviceCaseCustomer, isError: isServiceCaseCustomerError} = useGetCustomerV2Query(
    {customerId: serviceCase?.customerId ?? ''},
    {skip: not(serviceCase?.customerId)}
  );

  const stateFlag = useStateFlag(serviceCase?.state);

  const handleDiscard = () => {
    if (not(serviceCaseId)) {
      return navigate(workshopRoutes.serviceCases);
    }

    deleteServiceCase({serviceCaseId})
      .unwrap()
      .then(() => {
        navigate(workshopRoutes.serviceCases);
      })
      .catch(handleApiError);
  };

  const onScheduleServiceCase = async () => {
    if (not(serviceCaseId) || !hasBookingUrl) {
      return;
    }

    if (jobFormRef.current?.isDirty) {
      const isSaved = await jobFormRef.current.submit();
      if (!isSaved) {
        return;
      }
    }

    postServiceCaseCreateOrders({serviceCaseId})
      .unwrap()
      .then(() => {
        window.open(serviceCase?.bookingUrl as string, '_self');
      })
      .catch(handleApiError);
  };

  const onCreateServiceCase = async () => {
    if (not(serviceCaseId)) {
      return;
    }

    if (jobFormRef.current?.isDirty) {
      const isSaved = await jobFormRef.current.submit();
      if (!isSaved) {
        return;
      }
    }

    postServiceCaseCreateOrders({serviceCaseId})
      .unwrap()
      .then(() => {
        if (source === 'business-case' && isNotNilOrEmpty(serviceCase?.businessCaseId)) {
          navigate(
            composePath(businessCaseRoutes.serviceCases, {
              params: {id: serviceCase?.businessCaseId},
            })
          );
        } else {
          navigate(composePath(workshopRoutes.serviceCaseDetail, {params: {id: serviceCaseId}}));
        }
      })
      .catch(handleApiError);
  };

  const handleCreateServiceDraftCase = async () => {
    if (!vehicle?.type || !customerId) {
      return;
    }

    if (serviceCaseId) {
      return patchServiceCase({
        serviceCaseId,
        body: {
          customerId,
          vehicleId: vehicle?.id,
          make: vehicle.make,
          type: vehicle.type,
          modelFamily: vehicle.modelFamily,
          vin: vehicle.vin,
          registrationPlate: vehicle.vehicle.registrationPlate,
        },
      })
        .unwrap()
        .then((response) => {
          if (response?.serviceCaseId) {
            setCustomerVehicleChanged(false);
            navigate(
              composePath(workshopRoutes.draftServiceCase, {
                params: {id: response?.serviceCaseId ?? ''},
                queryParams: {
                  [queryParams.COMPONENT_SECTIONS_TAB]: workshopSearchParams.create.serviceJobs,
                },
              })
            );
          }
        })
        .catch(handleApiError);
    }

    const customers = await getVehicleCustomer({vehicleId: vehicle?.id})
      .unwrap()
      .catch(handleApiError);

    const hasCustomerConnection = find(
      (vehicleCustomer) => vehicleCustomer?.customerId === customerId,
      customers ?? []
    );
    const hasVehicleOperator = find(
      (vehicleCustomer) => vehicleCustomer?.customerType === OPERATOR_TYPE,
      customers ?? []
    );

    if (not(hasCustomerConnection) && not(hasVehicleOperator)) {
      postVehicleCustomer({
        body: {
          customerType: OPERATOR_TYPE,
          customerId,
          vehicleId: vehicle.id,
          eventId: vehicle.id,
          eventType: 'vehicle',
          dateFrom: getApiDateString(new Date()),
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    postServiceCase({
      body: {
        vehicleId: vehicle?.id,
        customerId:
          !hasCustomerConnection || !hasVehicleOperator
            ? customerId
            : String(hasVehicleOperator.customerId),
        make: vehicle.make,
        type: vehicle.type,
        modelFamily: vehicle.modelFamily,
        vin: vehicle.vin,
        registrationPlate: vehicle.vehicle.registrationPlate,
      },
    })
      .unwrap()
      .then((response) => {
        const draftServiceCasePath = composePath(workshopRoutes.draftServiceCase, {
          params: {id: response?.id ?? ''},
          queryParams: {
            [queryParams.COMPONENT_SECTIONS_TAB]: workshopSearchParams.create.serviceJobs,
          },
        });
        if (response?.additional_action?.includes('MERCEDES_XENTRY_LINK')) {
          setCustomerVehicleChanged(false);

          const serviceCaseId = response.id ?? '';

          postAvailableXentryOrders({serviceCaseId}).unwrap().catch(handleApiError);

          openDialog(
            <LinkXentry
              serviceCaseId={serviceCaseId}
              onClose={() => closeDialog('selectXentryDialog')}
              onLink={(xentryOrderId) =>
                postIntegrationsForXentryLink({serviceCaseId, body: {xentryOrderId}})
                  .unwrap()
                  .then(() => closeDialog('selectXentryDialog'))
                  .catch(handleApiError)
              }
              onLinkNew={() =>
                postServiceCaseIntegrationsXentryOrderManualCreation({
                  serviceCaseId,
                })
                  .unwrap()
                  .then(() => closeDialog('selectXentryDialog'))
                  .catch(handleApiError)
              }
              data-testid={testIds.workshop.createServiceCase('customerVehicleSelection')}
            />,
            {
              title: i18n.t('entity.integration.labels.selectXentryOrder'),
              id: 'selectXentryDialog',
              onCloseComplete: () => {
                navigate(draftServiceCasePath);
              },
            }
          );
        } else if (response?.id) {
          setCustomerVehicleChanged(false);
          navigate(draftServiceCasePath);
        }
      })
      .catch(handleApiError);
  };

  const handleTabChange = (newTab: string) => {
    if (
      newTab === workshopSearchParams.create.serviceJobs &&
      (sectionTab === workshopSearchParams.create.customerVehicle || isNil(sectionTab))
    ) {
      handleCreateServiceDraftCase();
    }
  };

  const onCustomerChange = (customerId: string | Nullish) => {
    setCustomerId(customerId);
    setCustomerVehicleChanged(true);
  };

  const onVehicleChange = (vehicleId: string | Nullish) => {
    setVehicleId(vehicleId);
    setCustomerVehicleChanged(true);
  };

  const actions = buildArray<Action>()
    .add({
      type: 'button',
      onClick: handleDiscard,
      title: i18n.t('general.actions.discard'),
      isLoading: deleteServiceCaseQuery.isLoading,
      isDisabled: isPostServiceCaseCreate,
      variant: 'secondary',
    })
    .add({
      type: 'custom',
      isDisabled: isPostServiceCaseCreate || deleteServiceCaseQuery.isLoading,
      component: <IntegrationsDropdown serviceCaseId={serviceCaseId} />,
    })
    .when(hasBookingUrl, {
      type: 'button',
      variant: 'secondary',
      onClick: onScheduleServiceCase,
      isLoading: isPostServiceCaseCreate,
      leftIcon: 'action/calendar_today',
      isDisabled:
        not(jobs?.length) ||
        sectionTab === workshopSearchParams.create.customerVehicle ||
        isPostServiceCaseCreate ||
        deleteServiceCaseQuery.isLoading,
      title: i18n.t('general.actions.schedule'),
    })
    .when(serviceCaseId, {
      type: 'button',
      variant: 'primary',
      onClick: onCreateServiceCase,
      isLoading: isPostServiceCaseCreate,
      isDisabled:
        not(jobs?.length) ||
        sectionTab === workshopSearchParams.create.customerVehicle ||
        isPostServiceCaseCreate ||
        deleteServiceCaseQuery.isLoading,
      title: i18n.t('general.actions.confirm'),
    });

  const flags = buildArray<FlagProps>()
    .when(serviceCaseId && stateFlag, stateFlag as FlagProps)
    .whenNot(serviceCaseId, {label: i18n.t('entity.serviceOrder.states.draft')});

  const parameters = useServiceCaseVehicleDraftParameters(
    serviceCaseId,
    serviceCase,
    vehicle,
    hasComments,
    additionalNote?.note
  );

  const controls = buildArray<EntityHeaderControl>().when(
    serviceCase?.authorizationProfileId,
    'AUTHORIZATION_PROFILE'
  );

  const isError =
    isServiceCaseError || isServiceCaseJobsError || isVehicleError || isServiceCaseCustomerError;

  const isLoading =
    isServiceCaseLoading ||
    isServiceCaseJobsLoading ||
    isPostServiceCaseLoading ||
    isPatchServiceCaseLoading ||
    isPostVehicleCustomerLoading;

  const isCustomerVehicleIconChecked = customerId && vehicleId;
  const isConnectedToBusinessCase = isNotNilOrEmpty(serviceCase?.businessCaseId);
  const isJobsIconChecked =
    not(isCustomerVehicleChanged) && serviceCaseId && isPositive(jobs?.length);

  const isNotNewOrEdited =
    serviceCase?.customerId === customerId && serviceCase?.vehicleId === vehicleId;

  const vehicleLocalActionNoteQuery = useGetVehicleLocalActionNoteQuery(
    {vehicleId},
    {skip: !vehicleId}
  );

  const recallMessage = vehicleLocalActionNoteQuery.currentData?.local_action_note_text;

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.newServiceCase')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight="100%">
        <Page
          isFullHeight
          header={
            <EntityHeader
              data-testid={testIds.workshop.createServiceCase('header')}
              title={
                isNotNewOrEdited
                  ? `${vehicle?.title} - ${getCustomerName(serviceCaseCustomer)}`
                  : i18n.t('page.workshop.labels.newServiceCase')
              }
              icon="action/build"
              flags={flags}
              actions={actions}
              parameters={isNotNewOrEdited ? parameters : undefined}
              controls={controls.length > 0 ? controls : undefined}
              authorizationProfileRecordId={serviceCase?.authorizationProfileId || undefined}
              alerts={recallMessage ? [{type: 'banner', message: recallMessage}] : undefined}
              recordId={serviceCase?.id || undefined}
            />
          }
          data-testid={testIds.workshop.createServiceCase()}
        >
          <Section
            isFullHeight
            defaultActiveTab="customerVehicle"
            data-testid={testIds.workshop.createServiceCase('sections')}
            onTabChange={handleTabChange}
            tabs={[
              {
                queryId: workshopSearchParams.create.customerVehicle,
                content: (
                  <CustomerVehicleSelection
                    customerId={customerId}
                    vehicleId={vehicleId}
                    onCustomerChange={onCustomerChange}
                    onVehicleChange={onVehicleChange}
                    onContinueClick={handleCreateServiceDraftCase}
                    data-testid={testIds.workshop.createServiceCase('customerVehicleSelection')}
                  />
                ),
                icon: isCustomerVehicleIconChecked ? 'success' : 'default',
                title: i18n.t('page.workshop.labels.customerVehicle'),
                isDisabled: isConnectedToBusinessCase && isNotNewOrEdited,
              },
              {
                queryId: workshopSearchParams.create.serviceJobs,
                content: (
                  <ServiceJobsSelection
                    isBackButtonDisabled={isConnectedToBusinessCase && isNotNewOrEdited}
                    onBackClick={() => setSectionTab(workshopSearchParams.create.customerVehicle)}
                    formRef={jobFormRef}
                  />
                ),
                title: i18n.t('page.workshop.labels.serviceRequests'),
                icon: isJobsIconChecked ? 'success' : 'default',
                isDisabled: not(customerId) || not(vehicleId),
              },
            ]}
          />
        </Page>
      </DataStatus>
    </>
  );
}
